import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./ReservationSuccess.module.scss";
import { AddToCalendarButton } from "../../../../../common/components/calendar/AddToCalendarButton";
import { routePaths } from "../../../../../common/infrastructure/routes/routePaths";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { NotificationPermissionDialog } from "../../../../notifications/NotificationPermissionDialog";
import { useAppContext } from "../../../../../AppContext";

interface IReservationSuccessViewModel {
  reservationId: number;
  eventName: string;
  instructor: string;
  date: string;
  startTime: string;
  endTime: string;
  hallName?: string;
  studioName: string;
  isWaitingList: boolean;
  isPendingPayment: boolean;
  prepaymentProvider?: number;
  userName: string;
  userPhone?: string;
  /** A remote (Google Meet) class. */
  isRemote?: boolean;
  /** The Meet link; the server fills it only for the client's own reservation. */
  meetingUrl?: string | null;
}

export const ReservationSuccess = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppContext();
  const [searchParams] = useSearchParams();
  const [celebrationActive, setCelebrationActive] = useState(false);

  const reservationId = searchParams.get("reservationId");

  const { data } = useApiQuery<IReservationSuccessViewModel>({
    queryKey: [
      endpointsMetadata.portalCalendar.getReservationDetails,
      reservationId,
    ],
    endpoint: `${endpointsMetadata.portalCalendar.getReservationDetails}?reservationId=${reservationId}`,
    enabled: !!reservationId,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: false,
  });

  const showDetails = !!data;
  const isWaitingList = data?.isWaitingList ?? false;
  const isPendingLinkPayment =
    (data?.isPendingPayment ?? false) && data?.prepaymentProvider !== 2;
  const meetingUrl = data?.meetingUrl?.trim() || null;
  // A waiting-list place is not a seat yet, so there is nothing to join.
  const canJoinMeeting = !!meetingUrl && !isWaitingList;

  useEffect(() => {
    if (!data) return;
    const timer = setTimeout(() => {
      setCelebrationActive(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [data]);

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("bg-BG", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (!showDetails) {
    return <div className={styles.container} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {/* Success Animation */}
        <div className={styles.successAnimation}>
          <div
            className={`${styles.successIcon}${
              celebrationActive && !isWaitingList && !isPendingLinkPayment
                ? ` ${styles.celebrating}`
                : ""
            }${isWaitingList || isPendingLinkPayment ? ` ${styles.waitingListIcon}` : ""}`}
          >
            <span className={styles.checkmark}>
              {isWaitingList ? "⏳" : isPendingLinkPayment ? "💳" : "✓"}
            </span>

            {celebrationActive && !isWaitingList && !isPendingLinkPayment && (
              <>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.1s",
                      "--x": "-20px",
                      "--y": "-30px",
                    } as React.CSSProperties
                  }
                >
                  🎉
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.2s",
                      "--x": "25px",
                      "--y": "-25px",
                    } as React.CSSProperties
                  }
                >
                  ✨
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.3s",
                      "--x": "-30px",
                      "--y": "20px",
                    } as React.CSSProperties
                  }
                >
                  🎊
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.4s",
                      "--x": "30px",
                      "--y": "25px",
                    } as React.CSSProperties
                  }
                >
                  ⭐
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.5s",
                      "--x": "0px",
                      "--y": "-40px",
                    } as React.CSSProperties
                  }
                >
                  🎈
                </div>
              </>
            )}
          </div>

          {isWaitingList ? (
            <>
              <h1 className={styles.successTitle}>
                Записахте се в списъка на чакащите
              </h1>
              <p className={styles.successSubtitle}>
                Местата за този клас са запълнени. Записахме ви в списъка на
                чакащите — ще получите имейл, ако се освободи място.
              </p>
            </>
          ) : isPendingLinkPayment ? (
            <>
              <h1 className={styles.successTitle}>
                Резервацията е в очакване на плащане
              </h1>
              <p className={styles.successSubtitle}>
                Ще получите имейл с инструкции как да платите за класа.
                Резервацията ви ще бъде потвърдена след успешно плащане.
              </p>
            </>
          ) : (
            <>
              <h1 className={styles.successTitle}>Ще ви очакваме! 🎉</h1>
              <p className={styles.successSubtitle}>
                Вашата резервация е потвърдена! Ще получите имейл за
                потвърждение.
              </p>
            </>
          )}
        </div>

        {/* Booking Details */}
        <div className={styles.bookingDetails}>
          <div className={styles.bookingHeader}>
            <h2 className={styles.bookingTitle}>Детайли за резервацията</h2>
          </div>

          <div className={styles.detailsGrid}>
            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>🏢</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Обект</span>
                <span className={styles.detailValue}>{data?.studioName}</span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>🏃</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Групово занимание</span>
                <span className={styles.detailValue}>{data?.eventName}</span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>👤</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Инструктор</span>
                <span className={styles.detailValue}>{data?.instructor}</span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>📅</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Дата и час</span>
                <span className={styles.detailValue}>
                  {formatDate(data?.date)} в {data?.startTime}
                </span>
              </div>
            </div>

            {data?.hallName && (
              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>📍</div>
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>Зала</span>
                  <span className={styles.detailValue}>{data.hallName}</span>
                </div>
              </div>
            )}

            {(data?.isRemote || !!meetingUrl) && (
              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>💻</div>
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>
                    Дистанционна среща
                  </span>
                  <span className={styles.detailValue}>
                    {canJoinMeeting
                      ? "Google Meet"
                      : "Линкът ще бъде в имейла и в „Моите резервации“."}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Customer Information */}
          {data.userName && (
            <div className={styles.customerCard}>
              <h3 className={styles.customerTitle}>Информация за клиента</h3>
              <div className={styles.customerDetails}>
                <div className={styles.customerInfo}>
                  <span className={styles.customerLabel}>Име:</span>
                  <span className={styles.customerValue}>{data.userName}</span>
                </div>
                {data.userPhone && (
                  <div className={styles.customerInfo}>
                    <span className={styles.customerLabel}>Телефон:</span>
                    <span className={styles.customerValue}>
                      {data.userPhone}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Important Reminders */}
        {/* <div className={styles.reminders}>
          <h3 className={styles.remindersTitle}>Важна информация</h3>
          <div className={styles.remindersList}>
            {isWaitingList ? (
              <>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>📧</span>
                  <span className={styles.reminderText}>
                    Ще получите имейл веднага щом се освободи място.
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⚡️</span>
                  <span className={styles.reminderText}>
                    Можете да следите статуса на резервацията си в секция "Моите
                    резервации"
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⏰</span>
                  <span className={styles.reminderText}>
                    Моля, пристигнете поне 5 минути преди часа на резервацията
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>📱</span>
                  <span className={styles.reminderText}>
                    Ще получите Email потвърждение след завършване на
                    резервацията
                  </span>
                </div>
                <div className={styles.reminderItem}>
                  <span className={styles.reminderIcon}>⚡️</span>
                  <span className={styles.reminderText}>
                    Можете да контролирате резервациитe си във Вашия профил.
                  </span>
                </div>
              </>
            )}
          </div>
        </div> */}

        {/* Action Buttons */}
        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            {!isWaitingList && (
              <AddToCalendarButton
                reservationId={
                  reservationId ? Number(reservationId) : undefined
                }
                className={styles.primaryButton}
              />
            )}
            {canJoinMeeting && (
              <a
                className={styles.secondaryButton}
                href={meetingUrl!}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={styles.buttonIcon}>💻</span>
                <span className={styles.buttonText}>Влез в срещата</span>
              </a>
            )}
            <button
              className={styles.secondaryButton}
              onClick={() => navigate(routePaths.portalMyReservations)}
              type="button"
            >
              <span className={styles.buttonIcon}>📊</span>
              <span className={styles.buttonText}>Моите резервации</span>
            </button>
          </div>
        </div>
      </div>
      <NotificationPermissionDialog
        isConfirmedReservation={
          isAuthenticated && !!data && !data.isWaitingList && !data.isPendingPayment
        }
        studioName={data?.studioName}
        eventDate={data?.date}
        startTime={data?.startTime}
      />
    </div>
  );
};
